import React, { useState, useEffect, useMemo } from "react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Seo from "../Seo";
import { faqPageJsonLd, breadcrumbJsonLd } from "../../seoSchema";
import "../../styles/FAQ.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import {
  resolveFirestoreImage,
  useCmsHeroImageReady,
} from "../../firestoreMedia";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
  authDomain: "wishuponaplayhouse.firebaseapp.com",
  projectId: "wishuponaplayhouse",
  storageBucket: "wishuponaplayhouse.firebasestorage.app",
  messagingSenderId: "513377368862",
  appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
  measurementId: "G-NZ116LS0Q1",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

const FAQ = () => {
  const [isOpen, setIsOpen] = useState([]);
  const [faqTitle, setFaqTitle] = useState("Frequently Asked Questions");
  const [faqItems, setFaqItems] = useState([
    {
      id: 1,
      question: "Are the playhouses waterproof?",
      answer:
        "Yes, your playhouse will not leak. However, the doors are not completely watertight, and a wind-driven rain may bring a trickle of water into the house.",
    },
    {
      id: 2,
      question: "Are these kits?",
      answer:
        "No, our playhouses are handcrafted and built on-site by master craftsmen. There is no assembly required by the owner, freight charges, additional materials to purchase, or costly cranes involved.",
    },
    {
      id: 3,
      question: "How long does it take to build the playhouse?",
      answer:
        "After your deposit is received, and a schedule has been set, a standard playhouse will take approximately three to five days to complete. Custom playhouses lead time will be determined by their scale and design.",
    },
    {
      id: 4,
      question: "What are the recommended ages?",
      answer:
        "Generally we recommend our playhouses for children ranging from 2 to 10 years of age. However, each child's imagination and play habits are different.",
    },
    {
      id: 5,
      question: "Can I change the paint colors?",
      answer:
        "Yes there are usually two to three color schemes to select for the exterior and one for the interior. We suggest selecting from the Benjamin Moore ® color palette the choice is yours.",
    },
    {
      id: 6,
      question: "Can the size of the playhouses be changed?",
      answer:
        "Yes you can increase or decrease the size of your playhouse. The order then becomes custom and a price change will be determined based on the size difference.",
    },
    {
      id: 7,
      question: "Are the windows operable?",
      answer:
        "Yes the windows are all single hung with tempered glass and screens. The Plexiglas window in the front door of The Cottage and The Sweet Shoppe are not operable.",
    },
    {
      id: 8,
      question: "Can you replicate the style of our home?",
      answer:
        "Yes we can design a miniature of your home or any custom style you can dream up.",
    },
  ]);
  const [isLoading, setIsLoading] = useState(true);
  const [heroImage, setHeroImage] = useState(null);

  useEffect(() => {
    // Initialize isOpen array based on the number of FAQ items
    setIsOpen(Array(faqItems.length).fill(false));

    const fetchFAQData = async () => {
      try {
        setIsLoading(true);
        const docRef = doc(firestore, "Pages", "FAQPage");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data.title) setFaqTitle(data.title);
          if (data.items && data.items.length > 0) {
            setFaqItems(data.items);
            setIsOpen(Array(data.items.length).fill(false));
          }
          if (data.heroImage) setHeroImage(data.heroImage);
        }
      } catch (error) {
        console.error("Error fetching FAQ data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFAQData();
    // eslint-disable-next-line
  }, []);

  const faqHeroUrl = resolveFirestoreImage(heroImage, "/images/ranch.png");
  const faqHeroReady = useCmsHeroImageReady(faqHeroUrl, !isLoading);

  const faqSchema = useMemo(() => faqPageJsonLd(faqItems), [faqItems]);
  const faqBreadcrumb = useMemo(
    () =>
      breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Playhouses", path: "/playhouses" },
        { name: "FAQ", path: "/playhouses/faq" },
      ]),
    [],
  );

  const toggleAnswer = (index) => {
    const newIsOpen = [...isOpen];
    newIsOpen[index] = !newIsOpen[index];
    setIsOpen(newIsOpen);
  };

  if (isLoading) {
    return (
      <div className="faq-page">
        <Navbar />
        <div className="loading">Loading FAQ content...</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="faq-page">
      <Seo
        title="Playhouse FAQ — Pricing, Timeline & Installation | Denver, CO"
        description="Common questions about ordering a custom children's playhouse in Denver and Colorado: pricing, lead times, on-site installation, materials, warranty, and more."
        path="/playhouses/faq"
        jsonLd={faqSchema}
        jsonLdId="faq-page"
      />
      <Seo jsonLd={faqBreadcrumb} jsonLdId="faq-breadcrumb" />
      <Navbar />

      <>
        {/* Hero */}
        <div className="sub-hero">
          <div
            className="sub-hero-bg"
            style={{
              backgroundImage: `url('${faqHeroUrl}')`,
              opacity: faqHeroReady ? 1 : 0,
              transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          />
          <div className="sub-hero-overlay" />
          <div className="sub-hero-content">
            <p className="sub-hero-eyebrow">Got Questions?</p>
            <h1 className="sub-hero-title">{faqTitle}</h1>
          </div>
        </div>

        {/* Accordion */}
        <div className="faq-content">
          {faqItems.map((item, index) => (
            <div
              key={item.id}
              className={`faq-item${isOpen[index] ? " faq-item--open" : ""}`}
            >
              <button
                className="faq-question"
                onClick={() => toggleAnswer(index)}
                aria-expanded={isOpen[index]}
              >
                <span>{item.question}</span>
                <span className="faq-icon">
                  {isOpen[index] ? (
                    <FontAwesomeIcon icon={faChevronUp} />
                  ) : (
                    <FontAwesomeIcon icon={faChevronDown} />
                  )}
                </span>
              </button>
              {isOpen[index] && <p className="faq-answer">{item.answer}</p>}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="faq-cta-band">
          <div className="faq-cta-inner">
            <h2 className="faq-cta-heading">Still Have Questions?</h2>
            <p className="faq-cta-sub">
              We're happy to help — reach out and we'll get back to you right
              away.
            </p>
            <a href="/contact" className="faq-cta-btn">
              <span>Contact Us</span>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M3 8H13M13 8L9 4M13 8L9 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </a>
          </div>
        </div>
      </>

      <Footer />
    </div>
  );
};

export default FAQ;
